import React, { useState } from 'react';
import { ShieldCheck, ShieldOff, Key, Copy, Download, Loader2, CheckCircle, Mail, Smartphone } from 'lucide-react';
import { authApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

type SetupStep = 'idle' | 'choose' | 'qr' | 'verify' | 'codes' | 'email-done';

export default function SecuritySettings() {
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  const [setupStep, setSetupStep] = useState<SetupStep>('idle');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const mfaEnabled = (user as any)?.mfa_enabled || false;
  const mfaMethod = (user as any)?.mfa_method || null;

  // ── Authenticator App (TOTP) ──

  const handleTotpSetup = async () => {
    setLoading(true);
    try {
      const res = await authApi.totpSetup();
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setSetupStep('qr');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Setup', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authApi.totpVerifySetup(verifyCode);
      setBackupCodes(res.data.backup_codes);
      setSetupStep('codes');
      refreshUser();
      showToast('2FA erfolgreich aktiviert!', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Code ungültig', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── E-Mail 2FA ──

  const handleEmailSetup = async () => {
    setLoading(true);
    try {
      await authApi.mfaEnableEmail();
      setSetupStep('email-done');
      refreshUser();
      showToast('E-Mail-2FA erfolgreich aktiviert!', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Disable ──

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.totpDisable(user?.email || '', disablePassword);
      setShowDisable(false);
      setDisablePassword('');
      setSetupStep('idle');
      refreshUser();
      showToast('2FA wurde deaktiviert', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Backup Codes ──

  const downloadBackupCodes = () => {
    const text = `CoachDesk365 - 2FA Backup-Codes\n${'='.repeat(35)}\n\nBewahren Sie diese Codes sicher auf!\nJeder Code kann nur einmal verwendet werden.\n\n${backupCodes.map((c, i) => `${i + 1}. ${c}`).join('\n')}\n\nGeneriert am: ${new Date().toLocaleString('de-DE')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coachdesk365-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    showToast('Backup-Codes kopiert', 'success');
  };

  const reset = () => {
    setSetupStep('idle');
    setBackupCodes([]);
    setVerifyCode('');
    setQrCode('');
    setSecret('');
  };

  const methodLabel = mfaMethod === 'email' ? 'E-Mail-Code' : 'Authenticator-App';

  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <h3 style={{ marginBottom: 8 }}>Zwei-Faktor-Authentifizierung (2FA)</h3>
      <p className="text-muted text-sm" style={{ marginBottom: 24 }}>
        Schützen Sie Ihr Konto mit einem zusätzlichen Sicherheitscode bei der Anmeldung.
      </p>

      {/* Status Banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
        borderRadius: 'var(--radius-sm)', marginBottom: 24,
        background: mfaEnabled ? 'rgba(91, 140, 90, 0.08)' : 'rgba(192, 86, 79, 0.08)',
        border: `1px solid ${mfaEnabled ? 'rgba(91, 140, 90, 0.2)' : 'rgba(192, 86, 79, 0.2)'}`,
      }}>
        {mfaEnabled
          ? <><ShieldCheck size={20} style={{ color: 'var(--success)' }} /><span style={{ fontWeight: 500, color: 'var(--success)' }}>2FA ist aktiviert ({methodLabel})</span></>
          : <><ShieldOff size={20} style={{ color: 'var(--danger)' }} /><span style={{ fontWeight: 500, color: 'var(--danger)' }}>2FA ist nicht aktiviert</span></>
        }
      </div>

      {/* ── Idle: Show method selection or disable ── */}
      {setupStep === 'idle' && !showDisable && (
        <>
          {!mfaEnabled ? (
            <button className="btn btn-primary" onClick={() => setSetupStep('choose')} disabled={loading}>
              <ShieldCheck size={16} /> 2FA aktivieren
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={() => setShowDisable(true)}>
              <ShieldOff size={16} /> 2FA deaktivieren
            </button>
          )}
        </>
      )}

      {/* ── Method Selection ── */}
      {setupStep === 'choose' && (
        <div>
          <h4 style={{ marginBottom: 16 }}>Methode wählen</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Authenticator App */}
            <button
              type="button"
              onClick={handleTotpSetup}
              disabled={loading}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                padding: '24px 16px', border: '2px solid var(--border)', borderRadius: 'var(--radius)',
                background: 'var(--bg-card)', cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
            >
              <Smartphone size={32} style={{ color: 'var(--primary)' }} />
              <strong style={{ fontSize: '0.95rem' }}>Authenticator-App</strong>
              <p className="text-muted text-sm" style={{ textAlign: 'center', margin: 0 }}>
                Google Authenticator, Authy, 1Password o.ae. — Code wird in der App generiert
              </p>
              <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 20, background: 'rgba(91, 140, 90, 0.1)', color: 'var(--success)', fontWeight: 500 }}>
                Empfohlen
              </span>
            </button>

            {/* Email Code */}
            <button
              type="button"
              onClick={handleEmailSetup}
              disabled={loading}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                padding: '24px 16px', border: '2px solid var(--border)', borderRadius: 'var(--radius)',
                background: 'var(--bg-card)', cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
            >
              <Mail size={32} style={{ color: 'var(--info)' }} />
              <strong style={{ fontSize: '0.95rem' }}>E-Mail-Code</strong>
              <p className="text-muted text-sm" style={{ textAlign: 'center', margin: 0 }}>
                Bei jeder Anmeldung wird ein 6-stelliger Code an Ihre E-Mail gesendet
              </p>
              <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 20, background: 'rgba(91, 124, 168, 0.1)', color: 'var(--info)', fontWeight: 500 }}>
                Einfach
              </span>
            </button>
          </div>
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setSetupStep('idle')}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* ── Email Done ── */}
      {setupStep === 'email-done' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <CheckCircle size={20} style={{ color: 'var(--success)' }} />
            <h4>E-Mail-2FA aktiviert!</h4>
          </div>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
            Bei jeder Anmeldung erhalten Sie einen 6-stelligen Sicherheitscode an <strong>{user?.email}</strong>.
          </p>
          <button className="btn btn-primary" onClick={reset}>Fertig</button>
        </div>
      )}

      {/* ── Disable confirmation ── */}
      {showDisable && (
        <div style={{ maxWidth: 400 }}>
          <p className="text-sm" style={{ marginBottom: 12, color: 'var(--danger)' }}>
            Achtung: Ohne 2FA ist Ihr Konto weniger geschützt.
          </p>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="disable-pw">Passwort bestätigen</label>
            {/* autocomplete=new-password prevents password manager from auto-filling */}
            <input id="disable-pw" type="password" autoComplete="new-password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} placeholder="Ihr Passwort" required autoFocus />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-danger" onClick={handleDisable} disabled={loading || !disablePassword}>
              {loading ? 'Wird deaktiviert...' : 'Deaktivieren'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* ── TOTP: QR Code ── */}
      {setupStep === 'qr' && (
        <div>
          <h4 style={{ marginBottom: 8 }}>Schritt 1: QR-Code scannen</h4>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
            Scannen Sie den QR-Code mit Ihrer Authenticator-App.
          </p>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200, border: '1px solid var(--border)', borderRadius: 8, padding: 8, background: 'white' }} />
          </div>
          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              QR-Code funktioniert nicht? Secret manuell eingeben
            </summary>
            <div style={{ marginTop: 8, padding: '10px 14px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
              {secret}
            </div>
          </details>
          <button className="btn btn-primary" onClick={() => setSetupStep('verify')}>Weiter</button>
          <button className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={() => { setSetupStep('choose'); setQrCode(''); setSecret(''); }}>Abbrechen</button>
        </div>
      )}

      {/* ── TOTP: Verify code ── */}
      {setupStep === 'verify' && (
        <form onSubmit={handleVerify} autoComplete="off">
          <h4 style={{ marginBottom: 8 }}>Schritt 2: Code verifizieren</h4>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
            Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.
          </p>
          <div style={{ maxWidth: 300, marginBottom: 16 }}>
            <input type="text" inputMode="numeric" autoComplete="off" maxLength={6} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" autoFocus style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || verifyCode.length !== 6}>
            {loading ? 'Wird geprüft...' : '2FA aktivieren'}
          </button>
          <button type="button" className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={() => setSetupStep('qr')}>Zurück</button>
        </form>
      )}

      {/* ── Backup codes ── */}
      {setupStep === 'codes' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <CheckCircle size={20} style={{ color: 'var(--success)' }} />
            <h4>2FA aktiviert! Backup-Codes speichern</h4>
          </div>
          <p className="text-sm" style={{ marginBottom: 16, color: 'var(--danger)' }}>
            Bewahren Sie diese Codes sicher auf! Jeder Code kann nur einmal verwendet werden, falls Sie den Zugang zu Ihrer Authenticator-App verlieren.
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px',
            padding: 16, background: 'var(--bg)', borderRadius: 'var(--radius-sm)',
            fontFamily: 'monospace', fontSize: '0.95rem', marginBottom: 16,
          }}>
            {backupCodes.map((c, i) => (
              <div key={i} style={{ padding: '4px 0' }}>
                <Key size={12} style={{ marginRight: 6, color: 'var(--text-muted)', verticalAlign: 'middle' }} />
                {c}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={downloadBackupCodes}>
              <Download size={16} /> Herunterladen
            </button>
            <button className="btn btn-secondary" onClick={copyBackupCodes}>
              <Copy size={16} /> Kopieren
            </button>
            <button className="btn btn-secondary" onClick={reset}>Fertig</button>
          </div>
        </div>
      )}

      {/* Info */}
      <div style={{ marginTop: 24, padding: '12px 16px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
        <strong>Authenticator-Apps:</strong> Google Authenticator, Microsoft Authenticator, Authy, 1Password, Bitwarden
      </div>
    </div>
  );
}
